import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, makeStyles, Theme, Typography } from '@material-ui/core'
import ClearOutlined from '@material-ui/icons/ClearOutlined'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { FC, useCallback, useState } from 'react'
import colors from '../../listingColors'
import PremiumInfoIconTooltip from '../PremiumInfoIconTooltip'
import { CLEAR, PROPERTIES } from './constants'
import {
    ADD_MANUFACTURER_OPTION_ICON_URL,
    MANUFACTURER_OPTIONS,
} from './PremiumPlus/constants'
import PPLProvider from './PremiumPlus/PPLProvider'
import PremiumPlusDialog from './PremiumPlus/PremiumPlusDialog'

type Props = {
    clearAllSelection(): void
    isPremiumAuthorized?: boolean
    listingType: ListingLevel
}

const {
    defaultLight,
    premiumPrimary,
    premiumSecondary,
    propertiesTitle,
} = colors

const useStyles = makeStyles((theme: Theme) => ({
    fullWidth: {
        width: '100%',
        borderBottom: `6px solid ${propertiesTitle}`,
        display: 'flex',
    },
    title: {
        marginTop: theme.spacing(1),
        color: propertiesTitle,
        fontWeight: 500,
        textTransform: 'uppercase',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        textAlign: 'right',
        paddingRight: theme.spacing(3),
    },
    addPremiumButton: {
        color: defaultLight,
        display: 'flex',
        alignItems: 'center',
        fontSize: pxToRem(13),
        paddingRight: theme.spacing(1.875),
        paddingLeft: theme.spacing(2.5),
        height: pxToRem(28),
        backgroundColor: premiumPrimary,
        border: `1px solid ${premiumPrimary}`,
        borderRadius: pxToRem(20),
        marginRight: theme.spacing(2.625),
        marginBottom: theme.spacing(0.5),
        fontWeight: 'normal',
        '&:hover': {
            backgroundColor: premiumSecondary,
            border: `1px solid ${premiumSecondary}`,
            color: defaultLight,
        },
    },
    clearButton: {
        padding: 0,
        color: theme.palette.primary.main,
        fontSize: pxToRem(16),
        fontWeight: 500,
        marginTop: theme.spacing(0.75),
        minWidth: pxToRem(97),
    },
    clearIcon: {
        border: '1px solid',
        borderRadius: 100,
        marginRight: theme.spacing(0.5),
        fontSize: pxToRem(16),
        fontWeight: 500,
        padding: 0,
    },
    addManufacturerButton: {
        marginRight: theme.spacing(1),
    },
    addManufacturerOptionIcon: {
        marginRight: theme.spacing(0.5),
        width: pxToRem(20),
        height: pxToRem(12),
    },
}))

export const PropertiesHeader: FC<Props> = ({
    clearAllSelection,
    isPremiumAuthorized = false,
    listingType,
}) => {
    const classes = useStyles()

    const [isPremiumMode, setIsPremiumMode] = useState(false)

    const handlePremiumClick = useCallback(() => {
        setIsPremiumMode(true)
    }, [])

    const handlePremiumClose = useCallback(() => {
        setIsPremiumMode(false)
    }, [])

    return (
        <GridItem className={classes.fullWidth}>
            {isPremiumMode && isPremiumAuthorized && (
                <PPLProvider>
                    <PremiumPlusDialog
                        isOpen={isPremiumMode}
                        onRequestClose={handlePremiumClose}
                    />
                </PPLProvider>
            )}
            <GridContainer alignItems="flex-end">
                <GridItem md={4} sm={4} xs={4}>
                    <Typography
                        variant="h6"
                        component="span"
                        className={classes.title}
                    >
                        {PROPERTIES}
                    </Typography>
                    {processEnv.REACT_APP_EnableBadgelessCards !== '1' && (
                        <PremiumInfoIconTooltip editMode />
                    )}
                </GridItem>
                <GridItem
                    md={8}
                    sm={8}
                    xs={8}
                    className={classes.buttonContainer}
                >
                    {isPremiumAuthorized &&
                        [
                            ListingLevel.Premium,
                            ListingLevel.PremiumMasterSpec,
                            ListingLevel.ProductListing,
                        ].includes(listingType) && (
                            <Button
                                className={clsx([
                                    classes.clearButton,
                                    classes.addManufacturerButton,
                                ])}
                                onClick={handlePremiumClick}
                                data-testid="openMODialogButton"
                            >
                                <img
                                    src={ADD_MANUFACTURER_OPTION_ICON_URL}
                                    className={
                                        classes.addManufacturerOptionIcon
                                    }
                                />
                                {MANUFACTURER_OPTIONS}
                            </Button>
                        )}
                    <Button
                        className={classes.clearButton}
                        onClick={clearAllSelection}
                        data-testid="clearSelectedOptionsButton"
                    >
                        <ClearOutlined className={classes.clearIcon} />
                        {CLEAR}
                    </Button>
                </GridItem>
            </GridContainer>
        </GridItem>
    )
}
